import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { CollectionState, ItemsState } from "@/types/Items";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isCollection = (value: unknown): value is CollectionState<unknown> =>
  isRecord(value);

/**
 * Untyped, browser-persisted selections keyed by collection name.
 *
 * @deprecated Read the client a page acts for from `the route client parameter` (the
 * route), and the signed-in user's role, client and locations from
 * `useAuthStore`. Do not add collections.
 */
export const useItemsStore = create<ItemsState>()(
  persist(
    (set) => ({
      collections: {},

      setSelectedItem: (collection, item) =>
        set((state) => ({
          collections: {
            ...state.collections,
            [collection]: {
              ...state.collections[collection],
              selectedItem: item,
              items: state.collections[collection]?.items ?? {},
            },
          },
        })),

      setSelectedItemId: (collection: string, id: string) =>
        set((state) => ({
          collections: {
            ...state.collections,
            [collection]: {
              ...state.collections[collection],
              selectedItem: id,
            },
          },
        })),

      addItem: (collection, id, item) =>
        set((state) => ({
          collections: {
            ...state.collections,
            [collection]: {
              ...state.collections[collection],
              items: {
                ...state.collections[collection]?.items,
                [id]: item,
              },
            },
          },
        })),

      removeItem: (collection, id) =>
        set((state) => {
          if (!state.collections[collection]?.items) return state;
          const { [id]: _, ...remainingItems } =
            state.collections[collection].items;
          return {
            collections: {
              ...state.collections,
              [collection]: {
                ...state.collections[collection],
                items: remainingItems,
              },
            },
          };
        }),
      removeSelectedItem: (collection: string) =>
        set((state) => ({
          collections: {
            ...state.collections,
            [collection]: {
              ...state.collections[collection],
              selectedItem: undefined,
            },
          },
        })),
      removeSelectedItemId: (collection: string) =>
        set((state) => ({
          collections: {
            ...state.collections,
            [collection]: {
              ...state.collections[collection],
              selectedItem: undefined,
            },
          },
        })),
      clearCollection: (collection) =>
        set((state) => {
          const { [collection]: _, ...remainingCollections } =
            state.collections;
          return {
            collections: remainingCollections,
          };
        }),

      clearAll: () => set({ collections: {} }),
    }),
    {
      name: "items-storage",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ collections: state.collections }),
      // Version 1 keeps no client: the route names it. A browser's stored
      // client selection from version 0 is dropped as it loads.
      version: 1,
      migrate: (persisted) => {
        const collections =
          isRecord(persisted) && isRecord(persisted["collections"])
            ? persisted["collections"]
            : {};
        return {
          collections: Object.fromEntries(
            Object.entries(collections).filter(
              (entry): entry is [string, CollectionState<unknown>] =>
                entry[0] !== "client" && isCollection(entry[1]),
            ),
          ),
        };
      },
    },
  ),
);
